import { Field, FormikProps } from 'formik';
import Tooltip from 'rc-tooltip';
import * as React from 'react';

import { GeneralInformationFormData } from './types';
import TextInput from '../input-text';

import CheckboxInput from '../input-checkbox';
import MaterialSelectInput from '../input-select';
import IconCheckboxInput from '../icon-checkbox';

export interface LocationSuggestions {
    markets: string[];
    regions: string[];
}

interface OwnProps {
    formikProps: FormikProps<GeneralInformationFormData>;

    // Whether or not to show international fields
    showIntenationalFields?: boolean; // implicit false

    // Whether or not to enable/disable cross region fields
    crossRegionEnabled?: boolean; // implcit false

    // Some text that describes why cross region fields are disabled
    crossRegionDisabledText?: string;

    allStateChoices: { value: string; label: string }[];
    stateMarketsMap: { [s: string]: string[] };
    allMarketChoices: { value: string; label: string }[];
    marketRegionsMap: { [s: string]: string[] };

    allCrossMarketChoices: { value: string; label: string }[];
    crossMarketRegionsMap: { [s: string]: string[] };

    allRegionChoices: { value: string; label: string }[];

    getLocationSuggestionsWithZipCode: (zip: string) => Promise<LocationSuggestions>;
}

interface OwnState {
    loadingLocationSuggestions: boolean;
}

export class AddressCard extends React.Component<OwnProps, OwnState> {
    constructor(props: OwnProps) {
        super(props);

        this.state = { loadingLocationSuggestions: false };
    }

    componentDidUpdate(prevProps: OwnProps) {
        // If DW is changed to False, reset the country and province fields
        if (!this.props.showIntenationalFields && prevProps.showIntenationalFields) {
            const initialVals = this.props.formikProps.initialValues;
            this.props.formikProps.setFieldValue('country', initialVals.country);
            this.props.formikProps.setFieldValue('province', initialVals.province);
        }
    }

    render() {
        const {
            formikProps: { values, errors, setFieldValue },
        } = this.props;

        return (
            <div className='VenueManagement--card'>
                <div className='VenueManagement--card--header'>Address</div>
                <div className='VenueManagement--card--body'>
                    <div className='VenueManagement--row'>
                        <div className='VenueManagement--col'>
                            <div className='VenueManagement--row'>
                                <div className='VenueManagement--col'>
                                    <Field label='Address 1' component={TextInput} name='address_1' value={values.address_1} />
                                </div>
                            </div>
                            <div className='VenueManagement--row'>
                                <div className='VenueManagement--col'>
                                    <Field label='Address 2' component={TextInput} name='address_2' value={values.address_2} />
                                </div>
                            </div>
                            <div className='VenueManagement--row'>
                                <div className='VenueManagement--col'>
                                    <Field label='City' component={TextInput} name='city' value={values.city} />
                                </div>
                                <div className='VenueManagement--col-divider-sm' />
                                <div className='VenueManagement--col'>
                                    <Field
                                        label='State'
                                        component={MaterialSelectInput}
                                        name='state'
                                        value={values.state}
                                        customOnChange={(oldVal: string | null, newVal: string | null) => {
                                            if (oldVal !== newVal) {
                                                setFieldValue('market', null);
                                                setFieldValue('region_1', null);
                                                setFieldValue('region_2', null);
                                            }
                                        }}
                                        choices={this.getStateChoices()}
                                    />
                                </div>
                            </div>
                            {!!this.props.showIntenationalFields && (
                                <div className='VenueManagement--row'>
                                    <div className='VenueManagement--col'>
                                        <Field label='Country' component={TextInput} name='country' value={values.country} />
                                    </div>
                                    <div className='VenueManagement--col-divider-sm' />
                                    <div className='VenueManagement--col'>
                                        <Field label='Province' component={TextInput} name='province' value={values.province} />
                                    </div>
                                </div>
                            )}
                        </div>
                        <div className='VenueManagement--col-divider' />
                        <div className='VenueManagement--col'>
                            <div>
                                <div className='VenueManagement--row'>
                                    <div className='VenueManagement--col'>
                                        <Field
                                            label='Zip code'
                                            component={TextInput}
                                            name='zip_code'
                                            value={values.zip_code}
                                            onBlur={(e: React.SyntheticEvent<HTMLInputElement>) => {
                                                this.getLocationSuggestions(e.currentTarget.value);
                                            }}
                                            indicator={this.state.loadingLocationSuggestions ? 'Searching...' : undefined}
                                        />
                                    </div>
                                    <div className='VenueManagement--col-divider-sm' />
                                    <div className='VenueManagement--col'>
                                        <div className='VenueManagement--lockable-field-container'>
                                            <Field
                                                label='Market'
                                                component={MaterialSelectInput}
                                                name='market'
                                                value={values.market}
                                                customOnChange={(oldVal: string | null, newVal: string | null) => {
                                                    if (oldVal !== newVal) {
                                                        setFieldValue('region_1', null);
                                                        setFieldValue('region_2', null);
                                                    }
                                                }}
                                                choices={this.getMarketChoices(values.state)}
                                            />
                                            <Field component={IconCheckboxInput} name='lock_market' hideErrors={true}>
                                                <Tooltip
                                                    prefixCls='rc-tooltip__dark'
                                                    placement='top'
                                                    visible={!!errors.lock_market}
                                                    overlay={<span>{errors.lock_market}</span>}
                                                >
                                                    <Tooltip
                                                        prefixCls='rc-tooltip__dark'
                                                        placement='top'
                                                        trigger={!!errors.lock_market ? [] : ['hover']}
                                                        overlay={<span>{!!values.lock_market ? 'Unlock' : 'Lock'}</span>}
                                                    >
                                                        <span className='icon-hb-nx-lock' />
                                                    </Tooltip>
                                                </Tooltip>
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className='VenueManagement--row'>
                                    <div className='VenueManagement--col'>
                                        <div className='VenueManagement--lockable-field-container'>
                                            <Field
                                                label='Region 1'
                                                component={MaterialSelectInput}
                                                name='region_1'
                                                value={values.region_1}
                                                choices={this.getRegionChoices(values.market)}
                                            />
                                            <Field component={IconCheckboxInput} name='lock_region_1' hideErrors={true}>
                                                <Tooltip
                                                    prefixCls='rc-tooltip__dark'
                                                    placement='top'
                                                    visible={!!errors.lock_region_1}
                                                    overlay={<span>{errors.lock_region_1}</span>}
                                                >
                                                    <Tooltip
                                                        prefixCls='rc-tooltip__dark'
                                                        placement='top'
                                                        trigger={!!errors.lock_region_1 ? [] : ['hover']}
                                                        overlay={<span>{!!values.lock_region_1 ? 'Unlock' : 'Lock'}</span>}
                                                    >
                                                        <span className='icon-hb-nx-lock' />
                                                    </Tooltip>
                                                </Tooltip>
                                            </Field>
                                        </div>
                                    </div>
                                    <div className='VenueManagement--col-divider-sm' />
                                    <div className='VenueManagement--col'>
                                        <div className='VenueManagement--lockable-field-container'>
                                            <Field
                                                label='Region 2'
                                                component={MaterialSelectInput}
                                                name='region_2'
                                                value={values.region_2}
                                                choices={this.getRegionChoices(values.market)}
                                            />
                                            <Field component={IconCheckboxInput} name='lock_region_2' hideErrors={true}>
                                                <Tooltip
                                                    prefixCls='rc-tooltip__dark'
                                                    placement='top'
                                                    visible={!!errors.lock_region_2}
                                                    overlay={<span>{errors.lock_region_2}</span>}
                                                >
                                                    <Tooltip
                                                        prefixCls='rc-tooltip__dark'
                                                        placement='top'
                                                        trigger={!!errors.lock_region_2 ? [] : ['hover']}
                                                        overlay={<span>{!!values.lock_region_2 ? 'Unlock' : 'Lock'}</span>}
                                                    >
                                                        <span className='icon-hb-nx-lock' />
                                                    </Tooltip>
                                                </Tooltip>
                                            </Field>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div>
                                <div className='VenueManagement--row'>
                                    <div className='VenueManagement--col'>
                                        <Tooltip
                                            prefixCls='rc-tooltip__dark'
                                            placement='top'
                                            trigger={!this.props.crossRegionEnabled ? ['click'] : []}
                                            overlay={
                                                <span>
                                                    {this.props.crossRegionDisabledText
                                                        ? `You require the following permissions to access
                                                             this field: ${this.props.crossRegionDisabledText}`
                                                        : 'You do not have sufficient permissions to edit this field'}
                                                </span>
                                            }
                                        >
                                            <div className='VenueManagement--lockable-field-container'>
                                                <Field
                                                    label='Cross region market'
                                                    component={MaterialSelectInput}
                                                    name='cross_region_market'
                                                    value={values.cross_region_market}
                                                    customOnChange={(oldVal: string | null, newVal: string | null) => {
                                                        if (oldVal !== newVal) {
                                                            setFieldValue('cross_region', null);
                                                        }
                                                    }}
                                                    choices={this.props.allCrossMarketChoices.map((item) => [item.value, item.label])}
                                                    disabled={!this.props.crossRegionEnabled}
                                                />
                                                <Field
                                                    component={IconCheckboxInput}
                                                    name='lock_cross_region_market'
                                                    disabled={!this.props.crossRegionEnabled}
                                                    hideErrors={true}
                                                >
                                                    <Tooltip
                                                        prefixCls='rc-tooltip__dark'
                                                        placement='top'
                                                        visible={!!errors.lock_cross_region_market}
                                                        overlay={<span>{errors.lock_cross_region_market}</span>}
                                                    >
                                                        <Tooltip
                                                            prefixCls='rc-tooltip__dark'
                                                            placement='top'
                                                            trigger={
                                                                this.props.crossRegionEnabled && !errors.lock_cross_region_market
                                                                    ? ['hover']
                                                                    : []
                                                            }
                                                            overlay={<span>{!!values.lock_cross_region_market ? 'Unlock' : 'Lock'}</span>}
                                                        >
                                                            <span className='icon-hb-nx-lock' />
                                                        </Tooltip>
                                                    </Tooltip>
                                                </Field>
                                            </div>
                                        </Tooltip>
                                    </div>
                                    <div className='VenueManagement--col-divider-sm' />
                                    <div className='VenueManagement--col'>
                                        <Tooltip
                                            prefixCls='rc-tooltip__dark'
                                            placement='top'
                                            trigger={!this.props.crossRegionEnabled ? ['click'] : []}
                                            overlay={
                                                <span>
                                                    {this.props.crossRegionDisabledText
                                                        ? `You require the following permissions to access
                                                             this field: ${this.props.crossRegionDisabledText}`
                                                        : 'You do not have sufficient permissions to edit this field'}
                                                </span>
                                            }
                                        >
                                            <div className='VenueManagement--lockable-field-container'>
                                                <Field
                                                    label='Cross region'
                                                    component={MaterialSelectInput}
                                                    name='cross_region'
                                                    value={values.cross_region}
                                                    choices={this.getCrossMarketRegionChoices(values.cross_region_market)}
                                                    disabled={!this.props.crossRegionEnabled}
                                                />
                                                <Field
                                                    component={IconCheckboxInput}
                                                    name='lock_cross_region'
                                                    disabled={!this.props.crossRegionEnabled}
                                                    hideErrors={true}
                                                >
                                                    <Tooltip
                                                        prefixCls='rc-tooltip__dark'
                                                        placement='top'
                                                        visible={!!errors.lock_cross_region}
                                                        overlay={<span>{errors.lock_cross_region}</span>}
                                                    >
                                                        <Tooltip
                                                            prefixCls='rc-tooltip__dark'
                                                            placement='top'
                                                            trigger={
                                                                this.props.crossRegionEnabled && !errors.lock_cross_region ? ['hover'] : []
                                                            }
                                                            overlay={<span>{!!values.lock_cross_region ? 'Unlock' : 'Lock'}</span>}
                                                        >
                                                            <span className='icon-hb-nx-lock' />
                                                        </Tooltip>
                                                    </Tooltip>
                                                </Field>
                                            </div>
                                        </Tooltip>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='VenueManagement--row'>
                        <Field
                            label='Keep address private'
                            component={CheckboxInput}
                            name='is_private_address'
                            checked={values.is_private_address}
                        />
                    </div>
                </div>
            </div>
        );
    }

    private getStateChoices() {
        return [['', '----']].concat(this.props.allStateChoices.map((item) => [item.value, item.label]));
    }

    private getMarketChoices(state?: string | null) {
        const {
            formikProps: { values },
        } = this.props;

        if (!state) {
            if (values.is_dw_venue) {
                return [['Destination Weddings', 'Destination Weddings']];
            } else {
                return [];
            }
        }

        // Shouldn't ever happen because state is a select, but who knows
        const mapping = this.props.stateMarketsMap[state];
        if (!mapping) {
            return [];
        }

        return this.props.allMarketChoices.filter((item) => mapping.indexOf(item.value) > -1).map((item) => [item.value, item.label]);
    }

    private getRegionChoices(market?: string | null) {
        if (!market) {
            return [];
        }

        const mapping = this.props.marketRegionsMap[market];
        if (!mapping) {
            return [];
        }

        return this.props.allRegionChoices.filter((item) => mapping.indexOf(item.value) > -1).map((item) => [item.value, item.label]);
    }

    private getCrossMarketRegionChoices(market?: string | null) {
        if (!market) {
            return [];
        }

        const mapping = this.props.crossMarketRegionsMap[market];
        if (!mapping) {
            return [];
        }

        return this.props.allRegionChoices.filter((item) => mapping.indexOf(item.value) > -1).map((item) => [item.value, item.label]);
    }

    private getLocationSuggestions(zip: string) {
        if (!zip) {
            return;
        }

        this.setState({ loadingLocationSuggestions: true });

        // Logic for dynamically suggesting new market/regions
        this.props
            .getLocationSuggestionsWithZipCode(zip)
            .then((res: LocationSuggestions) => {
                if (res.markets.length > 0) {
                    const { values, setFieldValue } = this.props.formikProps;

                    const curMarket = values.market;
                    const newMarket = res.markets[0];
                    let targetMarket = curMarket;

                    if (curMarket !== newMarket) {
                        if (!curMarket) {
                            setFieldValue('market', newMarket);
                            targetMarket = newMarket;
                        } else {
                            const marketChangeMsg =
                                `Zip code: ${zip} is associated with ${newMarket}` +
                                ` but the current market is set to ${curMarket}.` +
                                ` Do you want to change it?`;
                            if (confirm(marketChangeMsg)) {
                                setFieldValue('market', newMarket);
                                targetMarket = newMarket;
                            }
                        }
                    }

                    if (res.regions.length > 0) {
                        const curRegion = values.region_1;
                        const newRegion = res.regions[0];

                        // Make sure new region is part of the market
                        const validRegion = this.getRegionChoices(targetMarket)
                            .map((choice) => choice[0])
                            .some((region) => region === newRegion);

                        if (curRegion !== newRegion && validRegion) {
                            if (!curRegion) {
                                setFieldValue('region_1', newRegion);
                            } else {
                                const regionChangeMsg =
                                    `Zip code: ${zip} is associated with ${newRegion}` +
                                    ` but the current region is set to ${curRegion}.` +
                                    ` Do you want to change it?`;
                                if (confirm(regionChangeMsg)) {
                                    setFieldValue('region_1', newRegion);
                                }
                            }
                        }
                    }
                }
            })
            .catch()
            .then(() => this.setState({ loadingLocationSuggestions: false }));
    }
}
